/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState } from 'react'

const CookiesContext = createContext(null)

const parseDocumentCookies = () => {
  if (typeof document === 'undefined') {
    return {}
  }
  return document.cookie.split(';').reduce((accumulator, segment) => {
    const [name, ...valueParts] = segment.split('=')
    if (!name) {
      return accumulator
    }
    const key = name.trim()
    const value = valueParts.join('=').trim()
    if (!key) {
      return accumulator
    }
    try {
      accumulator[key] = decodeURIComponent(value)
    } catch {
      accumulator[key] = value
    }
    return accumulator
  }, {})
}

const serializeCookie = (name, value, options = {}) => {
  const encodedValue = encodeURIComponent(value)
  const attributes = []
  if (options.maxAge !== undefined) {
    attributes.push(`Max-Age=${Math.max(0, Number(options.maxAge))}`)
  }
  if (options.domain) {
    attributes.push(`Domain=${options.domain}`)
  }
  if (options.path) {
    attributes.push(`Path=${options.path}`)
  }
  if (options.secure) {
    attributes.push('Secure')
  }
  if (options.sameSite) {
    attributes.push(`SameSite=${options.sameSite}`)
  }
  if (options.httpOnly) {
    attributes.push('HttpOnly')
  }
  return `${name}=${encodedValue}${attributes.length ? `; ${attributes.join('; ')}` : ''}`
}

export function CookiesProvider({ children, defaultSetOptions = {} }) {
  const [cookies, setCookies] = useState(() => parseDocumentCookies())

  const setCookie = (name, value, options = {}) => {
    const cookieOptions = { ...defaultSetOptions, ...options }
    if (typeof document !== 'undefined') {
      document.cookie = serializeCookie(name, value, cookieOptions)
    }
    setCookies((previous) => ({ ...previous, [name]: value }))
  }

  const removeCookie = (name, options = {}) => {
    const cookieOptions = { ...defaultSetOptions, ...options, maxAge: 0 }
    if (typeof document !== 'undefined') {
      document.cookie = serializeCookie(name, '', cookieOptions)
    }
    setCookies((previous) => {
      const { [name]: _removed, ...rest } = previous
      return rest
    })
  }

  const contextValue = [cookies, setCookie, removeCookie]

  return <CookiesContext.Provider value={contextValue}>{children}</CookiesContext.Provider>
}

export const useCookies = (dependencies = []) => {
  const context = useContext(CookiesContext)
  if (!context) {
    throw new Error('useCookies must be used within a CookiesProvider')
  }

  const [cookies, setCookie, removeCookie] = context

  if (!dependencies || dependencies.length === 0) {
    return [cookies, setCookie, removeCookie]
  }

  const selectedCookies = dependencies.reduce((accumulator, key) => {
    if (key in cookies) {
      accumulator[key] = cookies[key]
    }
    return accumulator
  }, {})

  return [selectedCookies, setCookie, removeCookie]
}

export default CookiesProvider
